import { type ReactNode, useState } from 'react';
import { PhaseControls } from './PhaseControls';
import { CallsBadge } from './CallsBadge';
import { FindLinksBadge, FindLinksPanel } from './ListingSuggestions';
import { useListingCalls, useManualCalls } from './readCalls';
import { ListingsTable } from './ListingsTable';
import { useLanguage } from '../../contexts/LanguageContext';
import { startRange, useRun } from './categoryRun';
import { researchSubmodel, runListing, useListing, useSubmodels } from './listingStore';
import { submodelLabel } from './variantLabel';
import { useResolvedInfo } from './infoPicks';
import { newRun } from './docState';
import { stopRuns, useStopAsked } from './runHalt';

// Phase 1: what the manual and the listings SAY about the product, before anything is
// listed or drawn. The badges on top, then both sources, ONE PAGE, one under the other:
//   the MANUAL   — the PDF, its sections drawn on its pages, and the product's fields under
//                  it, in one box;
//   the LISTINGS — one table, a row per submodel: its link, its materials & colours, its
//                  pictures.
//
// They were two subphases behind a switcher. One page is shorter to read than two, and the
// listings lean on the manual (Find the links searches with what the manual read found), so
// the two are better seen together.
//
// The run itself (busy/progress/results) lives in categoryRun, keyed by document
// id, so it survives leaving and returning to the reader tab. This component just
// reads that shared state and renders it. The whole manual is always read (no page
// range). Lancer classifies every page and, with it, extracts the general info; the
// listing read joins in only when Phase 1 was given a URL — and the toggle lets the
// two be run apart. The brand they settle on is reused by Phase 2's part image search.
// What each badge's Run row says it reads.
const RUN_KEYS = {
  notice: 'notice.cat.searchNotice', listing: 'notice.cat.searchListing',
} as const;

export function CategoryTable({
  numPages,
  docId,
  file,
  locked,
  manual,
}: {
  numPages: number;
  docId: string;
  file: Blob;
  // While a "Run all" pipeline is active, the manual controls are disabled so a
  // step can't be relaunched out of band (the run button just greys out).
  locked?: boolean;
  // The manual itself — the PDF and the product fields — shown in the Manual subphase. Built by the viewer (see
  // PhaseCtx.manual).
  manual: ReactNode;
}) {
  const { t } = useLanguage();
  const { busy, active, cellErrors, error } = useRun(docId);
  const listing = useListing(docId);
  const submodels = useSubmodels(docId);
  // The manual-info read's own state (its error is shown under the product fields, in
  // UploadPanel).
  // Whether the two sources still owe the user a decision — the banner below, and
  // the gate that keeps this phase (and "Run all") from moving on.
  const { blocked } = useResolvedInfo(docId);

  // Whether there is a listing to read at all. Without one there is a single thing to
  // run — the manual — and the two narrower buttons are not offered.
  const hasListing = !!listing.url;
  // Any of the three reads in flight — the classification (busy), the info read, or the
  // listing read — locks the controls so a second launch can't overlap.
  const anyBusy = !!busy || listing.detecting;
  const stopAsked = useStopAsked(docId, anyBusy);

  // The button trails "· N en cours" — the number of Gemini calls in flight RIGHT
  // NOW across everything Lancer launched: the classification blocks (`active`,
  // which is per-block and per-model), the general-info read, and one per submodel
  // listing read still going (each link is its own call). Counting the submodels
  // individually, not the representative's one "any reading?" flag, so four links
  // reading read as four, not one.
  // (The page-classification "done/total" was dropped: it only tracked one of the
  // reads, so it read as the whole phase's progress when it wasn't.)
  const inFlight =
    (busy === 'range' ? active : 0) +
    submodels.filter((s) => s.detecting).length;
  const progressSuffix = inFlight > 0 ? ` · ${inFlight} ${t('notice.cat.inFlight')}` : '';
  // HOW LONG THE READ HAS BEEN GOING is the badge's own clock, on its Run row beside this
  // count: a clock that moves while the count sits still is a read taking its time, which is
  // a different thing from a page that has died.

  // Every call each read makes, for its badge.
  const manualCalls = useManualCalls(docId, file, numPages);
  const listingCalls = useListingCalls(docId);

  // Run one or both reads. The manual read classifies every page and re-extracts the
  // general info; a fresh run holds it, for the same reason "Run all" opens one — the last
  // answer stays browsable from Phase 1 and the run carries every other phase's answers
  // forward. The listing read is not run-scoped, so running it alone opens no run.
  //
  // WHICH ONE IS THE BUTTON, not a mode set beforehand. It was a three-way toggle above a
  // Run button: two gestures for one decision, and the setting persisted silently between
  // runs, so what the button would do depended on something chosen ten minutes ago. Now
  // each control says what it will run and nothing is remembered.
  // Which button was pressed, so the spinner and the in-flight count sit on it rather
  // than on all three at once. Cleared by the next press; while nothing is running it
  // says nothing, because `anyBusy` is what decides whether a spinner is drawn at all.
  const [launched, setLaunched] = useState<'notice' | 'listing' | null>(null);

  const onRun = (mode: 'notice' | 'listing') => {
    setLaunched(mode);
    void (async () => {
      if (mode === 'notice') {
        // A fresh run: newRun forks the current one server-side but drops the downstream
        // phases (parts / generated images / steps), so Phase 2/3/4 start blank for this run
        // instead of showing the last run's — the re-read below fills Phase 1/2 back in.
        await newRun(docId);
        void startRange(docId, file, 1, numPages, t);
      }
      if (mode === 'listing') {
        void runListing(docId, t);
      }
    })();
  };

  // ONE LINK, from the Listings badge: its Run offers every link or any one of them — the
  // choice the "Re-run search" button under each gallery used to be, there with the prompts
  // that read will send rather than under the pictures it replaces.
  const linkOptions = [
    { key: 'all', label: t('notice.cat.runAllLinks'), onRun: () => onRun('listing') },
    ...submodels.flatMap((s, i) => (s.url ? [{
      key: s.id,
      label: submodelLabel(s, i, t('notice.listing.label')),
      onRun: () => {
        setLaunched('listing');
        void researchSubmodel(docId, s.id, t);
      },
    }] : [])),
  ];

  // ONE READ'S BADGE: its calls walked in the panel, and its Run row. Always drawn, and
  // greyed out while it cannot run: Listings with no link to read.
  const badge = (mode: 'notice' | 'listing') => (
    <CallsBadge
      title={t(mode === 'notice' ? 'notice.cat.badgeManual' : 'notice.cat.badgeListings')}
      named
      entries={mode === 'notice' ? manualCalls : listingCalls}
      // The calls in flight on the Run row, while this read is the one out. The badge keeps
      // its own clock beside it.
      run={{
        label: `${t(RUN_KEYS[mode])}${anyBusy && launched === mode ? progressSuffix : ''}`,
        onRun: () => onRun(mode),
        busy: anyBusy && launched === mode,
        disabled: anyBusy || numPages < 1 || locked || (mode === 'listing' && !hasListing),
        ...(mode === 'listing' ? { options: linkOptions } : {}),
        // STOP, where this read was started on its own: under a "Run all" the chain's
        // Pause is the stop (see the note below).
        onStop: locked ? undefined : () => stopRuns(docId),
        ...(stopAsked && launched === mode ? { stopping: t('notice.stop.asked') } : {}),
      }}
    />
  );

  const failedCount = Object.values(cellErrors).reduce((sum, byPage) => sum + Object.keys(byPage).length, 0);

  return (
    <div className="mt-6 flex flex-col gap-6">
      {/* Controls, on top: one button per source, each of which runs it. The whole manual
          is always read — there is no page range to choose. */}
      <PhaseControls>
        {/* ONE BADGE PER READ, where there were three buttons: each is the usual menu — Run,
            then the prompt, reasoning & output of every call that Run makes, walked one at a
            time (a block of pages, a listing). What the reads answered used to hang off the
            answers as brain badges; it is here now, beside the question that produced it.

            The manual's alone here: "Find the links" and Listings each sit over their own box,
            below the manual. There is no "both" badge: it was the two in one click, and
            "Run all" still runs the two together. */}
        {badge('notice')}
        {/* UNDER A "RUN ALL", NO STOP HERE. It used to abort the CLASSIFICATION alone, which
            is half of what this phase runs: the chain marched on into Phase 2 and extracted
            parts from a half-classified manual. Phase 1's Pause governs a chain. A read
            started on its own stops from its badge (see onStop above), which halts every
            read this phase has out, the classification and the links alike. */}
      </PhaseControls>

      {/* Where the manual and the listing disagree, this phase stops until the user says
          which answer to keep — including for "Run all". ABOVE the switcher, not inside
          the Manual card where the disagreement itself is: it is why the whole phase has
          halted, and a reason you can only see by opening the right view is a reason
          nobody reads. Settling it is one click away, on the card beside it. */}
      {blocked && (
        <p className="mx-auto max-w-xl rounded-lg border border-rose-400 bg-rose-50 px-4 py-2 text-center text-sm font-medium text-rose-800">
          {t('notice.info.pickPrompt')}
        </p>
      )}

      {/* The classification's own failures, over the manual they are about. NO TABLE OF
          SECTIONS: it had a row per page saying what the page holds, and the PDF says the
          same thing on the page itself — every section boundary is drawn across it with its
          name. */}
      {error && <p className="-mt-2 text-center text-sm text-rose-600">{error}</p>}
      {failedCount > 0 && (
        <p className="-mt-2 text-center text-sm text-rose-600">
          {failedCount} {t('notice.cat.failed')}
        </p>
      )}

      {/* THE MANUAL — the PDF and the product fields under it (see UploadPanel) — then the
          listings under it. */}
      {manual}
      {/* FIND THE LINKS, over its own box: it searches with what the manual above read, and
          fills the listings below. Its own PhaseControls, so the demo hides it and the
          sandbox locks it like every other run badge. Drawn whether or not a listing exists
          yet — finding one is its job. */}
      <PhaseControls>
        <FindLinksBadge docId={docId} file={file} disabled={locked} />
      </PhaseControls>
      <FindLinksPanel docId={docId} disabled={locked} />
      {/* LISTINGS, over the table it fills. Its own PhaseControls, like the others. */}
      <PhaseControls>{badge('listing')}</PhaseControls>
      <ListingsTable docId={docId} />
    </div>
  );
}
